import { coreApi, stockApi } from '$api'
import { ISupplier, Supplier } from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { SupplierPatchRequest } from '@loadedhub/loaded-stock-api-sdk'
import dayjs from 'dayjs'

enum QueryKeys {
  getAllVerifiedSuppliers = 'getAllVerifiedSuppliers',
  getAllSuppliers = 'getAllSuppliers',
}

/**
 * Retrieves all verified suppliers from auth server
 * @returns an list of verified suppliers
 */
export const getVerifiedSuppliers = async (): Promise<ISupplier[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getAllVerifiedSuppliers],
    queryFn: async () => {
      return await coreApi.getVerifiedSuppliers()
    },
  })
}

/**
 * Retrieves all suppliers
 * @returns an list of suppliers
 */
export const getAllSuppliers = async (
  includeDeleted?: boolean
): Promise<ISupplier[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.getAllSuppliers, includeDeleted],
    queryFn: async ({ signal }) => {
      return await stockApi.getSuppliers(includeDeleted, signal)
    },
  })
}

/**
 * Creates a new supplier
 * @param newSupplier the new supplier to be created
 * @returns the created supplier
 */
export const createSupplier = async (
  newSupplier: ISupplier
): Promise<ISupplier> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error(
      "The user doesn't have permission to create a new supplier."
    )
  }

  return await stockApi.createSupplier(new Supplier(newSupplier))
}

export const updateSupplierThirdPartyContactId = async (
  supplierId: string,
  thirdPartyContactId: string | undefined
): Promise<ISupplier | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.updateSupplierThirdPartyContactId(
    supplierId,
    new SupplierPatchRequest({ thirdPartyContactId })
  )
}

/**
 * Updates a supplier
 * @param supplier the supplier to be updated
 * @returns the updated supplier
 */
export const updateSupplier = async (
  supplier: ISupplier
): Promise<ISupplier | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  return await stockApi.updateSupplier(supplier.id!, new Supplier(supplier))
}

/**
 * Soft deletes a supplier by setting removedAt timestamp
 * @param supplier the supplier to delete
 * @returns the updated supplier
 */
export const deleteSupplier = async (
  supplier: ISupplier
): Promise<ISupplier | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  // Set removedAt timestamp to mark as deleted
  const updatedSupplier = new Supplier({
    ...supplier,
    removedAt: dayjs(),
  })

  return await stockApi.updateSupplier(supplier.id!, updatedSupplier)
}

/**
 * Restores a soft-deleted supplier by clearing removedAt timestamp
 * @param supplier the supplier to restore
 * @returns the updated supplier
 */
export const restoreSupplier = async (
  supplier: ISupplier
): Promise<ISupplier | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  // Clear removedAt to restore
  const updatedSupplier = new Supplier({
    ...supplier,
    removedAt: undefined,
  })

  return await stockApi.updateSupplier(supplier.id!, updatedSupplier)
}

/**
 * Merges two suppliers - consolidates source supplier into destination supplier
 * @param sourceId the ID of the source supplier to merge from
 * @param destId the ID of the destination supplier to merge into
 * @returns object containing both updated suppliers
 */
export const mergeSuppliers = async (
  sourceId: string,
  destId: string
): Promise<{ source: ISupplier; destination: ISupplier } | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  const response = await stockApi.mergeSuppliers(sourceId, destId)

  if (!response.source || !response.destination) {
    throw new Error('Merge suppliers response is missing source or destination')
  }

  return {
    source: response.source,
    destination: response.destination,
  }
}
